import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL as API_URL } from '@/lib/backendUrl';

const REF_STORAGE_KEY = 'astrofeel_ref_code';
const REF_COOKIE_NAME = 'astrofeel_ref_code';
const REF_TTL_DAYS = 30;
// ============================================================
// EARLY CAPTURE: runs at import time, BEFORE React renders.
// This guarantees ref is saved even if React redirects the URL.
// ============================================================
(function captureRefFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (!refCode || !refCode.trim()) return;

    const code = refCode.trim();

    // Check if ref already saved (first referrer wins)
    const existing = _readRefFromLocalStorage();
    if (existing) {
      console.log(`[RefTracker:EARLY] Already stored ref=${existing}, ignoring new ref=${code}`);
      return;
    }

    // Also check cookie
    const existingCookie = _readRefFromCookie();
    if (existingCookie) {
      console.log(`[RefTracker:EARLY] Cookie has ref=${existingCookie}, ignoring new ref=${code}`);
      return;
    }

    // Save to localStorage
    const expiry = Date.now() + (REF_TTL_DAYS * 24 * 60 * 60 * 1000);
    const sessionId = `rs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const refData = { code, expiry, savedAt: new Date().toISOString(), sessionId };
    localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(refData));

    // Save to cookie
    const maxAge = REF_TTL_DAYS * 24 * 60 * 60;
    document.cookie = `${REF_COOKIE_NAME}=${encodeURIComponent(code)}; path=/; max-age=${maxAge}; SameSite=Lax`;

    console.log(`[RefTracker:EARLY] Saved ref=${code} session=${sessionId} from URL`);
  } catch (e) {
    console.error('[RefTracker:EARLY] Error:', e);
  }
})();

// ============================================================
// React component: handles backend tracking + assignment
// ============================================================
export default function RefTracker() {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (!refCode || !refCode.trim()) return;

    const code = refCode.trim();
    const stored = getStoredRefCode();

    // The early capture already saved it, but let's ensure
    if (!stored) {
      const expiry = Date.now() + (REF_TTL_DAYS * 24 * 60 * 60 * 1000);
      const sessionId = `rs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const refData = { code, expiry, savedAt: new Date().toISOString(), sessionId };
      localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(refData));
      const maxAge = REF_TTL_DAYS * 24 * 60 * 60;
      document.cookie = `${REF_COOKIE_NAME}=${encodeURIComponent(code)}; path=/; max-age=${maxAge}; SameSite=Lax`;
      console.log(`[RefTracker:useEffect] Saved ref=${code} (early capture missed)`);
    } else if (stored !== code) {
      console.log(`[RefTracker:useEffect] Stored ref=${stored} differs from URL ref=${code}, keeping first`);
      return;
    }

    // Track click on backend (fire-and-forget)
    let sessionId = null;
    try {
      const refData = JSON.parse(localStorage.getItem(REF_STORAGE_KEY) || '{}');
      sessionId = refData.sessionId || null;
    } catch {}

    fetch(`${API_URL}/api/ref/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref_code: code, session_id: sessionId })
    }).catch(() => {});

    // If user is already logged in, try to assign referrer
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      fetch(`${API_URL}/api/ref/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ ref_code: code })
      }).then(r => r.json()).then(d => {
        console.log(`[RefTracker:useEffect] assign result:`, d);
      }).catch(() => {});
    }
  }, [searchParams]);

  return null;
}

// ============================================================
// Helpers: read ref from localStorage / cookie
// ============================================================
function _readRefFromLocalStorage() {
  try {
    const stored = localStorage.getItem(REF_STORAGE_KEY);
    if (!stored) return null;
    const refData = JSON.parse(stored);
    if (Date.now() < refData.expiry) return refData.code;
    localStorage.removeItem(REF_STORAGE_KEY);
  } catch {}
  return null;
}

function _readRefFromCookie() {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === REF_COOKIE_NAME && value) {
        return decodeURIComponent(value);
      }
    }
  } catch {}
  return null;
}

/**
 * Get stored referral code. Checks localStorage first, then cookie.
 * Returns null if expired or not found.
 */
export function getStoredRefCode() {
  const fromLS = _readRefFromLocalStorage();
  if (fromLS) {
    console.log(`[RefTracker:getStoredRefCode] Found in localStorage: ${fromLS}`);
    return fromLS;
  }

  const fromCookie = _readRefFromCookie();
  if (fromCookie) {
    console.log(`[RefTracker:getStoredRefCode] Found in cookie: ${fromCookie}`);
    // Re-save to localStorage (it may have been cleared)
    try {
      const expiry = Date.now() + (REF_TTL_DAYS * 24 * 60 * 60 * 1000);
      const refData = { code: fromCookie, expiry, savedAt: new Date().toISOString(), sessionId: null, restoredFromCookie: true };
      localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(refData));
      console.log(`[RefTracker:getStoredRefCode] Restored to localStorage from cookie`);
    } catch {}
    return fromCookie;
  }

  console.log(`[RefTracker:getStoredRefCode] No ref code found`);
  return null;
}

/**
 * Get the stored session ID (for click tracking).
 */
export function getStoredRefSessionId() {
  try {
    const stored = localStorage.getItem(REF_STORAGE_KEY);
    if (stored) {
      const refData = JSON.parse(stored);
      return refData.sessionId || null;
    }
  } catch {}
  return null;
}

/**
 * Clear stored referral code (call after successful registration).
 */
export function clearStoredRefCode() {
  console.log('[RefTracker:clearStoredRefCode] Clearing stored ref code');
  localStorage.removeItem(REF_STORAGE_KEY);
  document.cookie = `${REF_COOKIE_NAME}=; path=/; max-age=0`;
}
